/**
 * GenericInlineDetailPanel — A doctype-agnostic inline detail row
 * that shows key fields from the row data in a compact card layout.
 * Renders as <tr> rows matching the parent table's column structure.
 */

import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { buildDetailRoute } from "@/lib/config/docRoutes";
import { ExternalLink, X } from "lucide-react";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";

export interface GenericInlineDetailPanelProps {
  doctype: string;
  docName: string;
  rowData: Record<string, unknown>;
  detailPath?: string;
  onClose?: () => void;
  columns: SubRowColumnInfo;
}

/** Fields to exclude from the detail display */
const EXCLUDED_FIELDS = new Set([
  "name", "owner", "creation", "modified", "modified_by",
  "idx", "docstatus", "doctype", "_user_tags", "_comments",
  "_assign", "_liked_by", "__islocal", "__unsaved",
  "_comment_count", "image", "primary_image", "logo", "icon",
]);

/** Format a field value for display */
function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  if (value === 1 || value === 0) return value === 1 ? "Yes" : "No";
  const str = String(value);
  // Truncate long values
  return str.length > 80 ? str.substring(0, 77) + "..." : str;
}

/** Convert fieldname to readable label */
function fieldnameToLabel(fieldname: string): string {
  return fieldname
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const PANEL_KEYFRAMES = `
@keyframes gidp-slide-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}`;

export function GenericInlineDetailPanel({
  doctype,
  docName,
  rowData,
  detailPath,
  onClose,
  columns,
}: GenericInlineDetailPanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => closeRef.current?.focus(), 160);
    return () => clearTimeout(timer);
  }, []);

  // Build field entries from row data, excluding system fields
  const fieldEntries = Object.entries(rowData)
    .filter(([key]) => !EXCLUDED_FIELDS.has(key))
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .slice(0, 12); // Cap at 12 fields to keep it compact

  const resolvedDetailPath = detailPath || buildDetailRoute(doctype, docName);
  const { columnCount } = columns;

  return (
    <>
      <style>{PANEL_KEYFRAMES}</style>
      <tr
        role="row"
        aria-label={`Details for ${docName}`}
        className="border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/60 dark:bg-neutral-900/40"
        style={{ animation: "gidp-slide-in 200ms ease-out" }}
      >
        <td colSpan={columnCount} className="p-0">
          <div className="px-4 py-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {doctype} Details
                </span>
                <Link
                  to={resolvedDetailPath}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-muted-foreground hover:text-foreground border border-neutral-200 dark:border-neutral-700 transition-colors"
                >
                  Open full detail
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
              {onClose && (
                <button
                  ref={closeRef}
                  type="button"
                  onClick={onClose}
                  className="p-1 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-700 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Close details"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Field grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2">
              {fieldEntries.map(([key, value]) => (
                <div key={key} className="min-w-0">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider truncate">
                    {fieldnameToLabel(key)}
                  </div>
                  <div className="text-xs text-foreground truncate" title={String(value)}>
                    {formatValue(value)}
                  </div>
                </div>
              ))}
            </div>

            {fieldEntries.length === 0 && (
              <div className="text-xs text-muted-foreground italic">
                No additional details available
              </div>
            )}
          </div>
        </td>
      </tr>
    </>
  );
}
